import { useState } from 'react';
import { DEFAULT_RACK_WEIGHTS_BY_LEVEL } from '../../../constants/warehouseLocations';
import Button from '../../ui/Button';
import Input from '../../ui/Input';
import { Mono } from '../../ui/Mono';
import Select from '../../ui/Select';
import { ParseDecimalInput, ParseIntegerInput } from '../locationFields';
import { useWarehouseLabels } from '../warehouseLabels';
import {
    BAY_SIDES_BY_ORIENTATION,
    CoerceBaySideForOrientation,
    GROUND_LEVEL,
    RACK_DRAFT_DEFAULTS,
    RACK_REAL_MAX_BAYS,
    RACK_REAL_MAX_LEVELS,
} from './mapModel';

/**
 * Painel ÚNICO de criação de RACK (porta-pallet): é o que o chip "Rack" da
 * paleta do editor abre, na coluna lateral, DEPOIS do traçado: chip → 1º
 * clique (início) → 2º clique (fim) → painel. Por isso NÃO tem "Orientação"
 * nem "Comprimento": os dois saem do traçado (`DeriveRackAxis`). O
 * `orientation` chega como PROP derivada — serve para "de que lado ficam os
 * blocos" oferecer o par certo (vertical = esquerda/direita; horizontal =
 * cima/baixo). Clicar no mapa de novo refaz o traçado sem perder o que já foi
 * digitado; ESC cancela tudo.
 *
 * NÃO EXISTE MAIS RUA (02/09/2026): o painel pergunta o PREFIXO do rack (1–2
 * letras, único), QUANTOS BLOCOS (sequenciais 1..N ao longo do traçado; em
 * branco = comprimento do traçado) e os ANDARES. Um lado só — sem ímpar/par.
 *
 * Criar o rack aqui cria DADOS REAIS — o CONFIRMAR chama o POST
 * generate-rack antes de desenhar. Por isso o form tem os campos do gerador:
 * PESOS POR ANDAR (kg) e a LARGURA DO BLOCO (m). O A1 é o térreo e NÃO tem
 * limite de peso (o pallet apoia no chão): ele fica FORA da gestão de pesos —
 * a lista começa no A2. Andares reais têm 1 dígito (máx. 9).
 *
 * `placeOnly` = "posicionar um rack que JÁ existe no cadastro": o prefixo e o
 * nº de blocos vêm do dado real (travados) e não há geração — só o desenho.
 *
 * O painel é dono dos RASCUNHOS (strings dos inputs); a cada tecla ele
 * normaliza (coerções dos defaults) e sobe `onChange(opts)` — o WarehouseMap
 * simula a criação no modelo (CreateRackInLayout numa cópia) e devolve em
 * `error` a PRIMEIRA violação (prefixo repetido, fora do grid, célula
 * ocupada, erro do generate como 409…): a pessoa vê o problema ANTES de criar.
 */

function defaultWeightForLevel(level) {
    return (
        DEFAULT_RACK_WEIGHTS_BY_LEVEL[level - 1] ??
        DEFAULT_RACK_WEIGHTS_BY_LEVEL[DEFAULT_RACK_WEIGHTS_BY_LEVEL.length - 1]
    );
}

/** Coerções dos defaults — aplicadas AO VIVO. `weights` é indexado por
 * andar−1 (o A1 fica no array para a conta ser simples, mas nunca é mostrado
 * nem enviado). A orientação é a DERIVADA que o mapa passa, e o lado dos blocos
 * é coagido a ela. */
export function NormalizeRackDraft(raw) {
    const orientation = BAY_SIDES_BY_ORIENTATION[raw.orientation] ? raw.orientation : RACK_DRAFT_DEFAULTS.orientation;
    let levels = ParseIntegerInput(raw.levels);
    if (!Number.isFinite(levels) || levels < 1) {
        levels = RACK_DRAFT_DEFAULTS.levels;
    }
    levels = Math.min(levels, RACK_REAL_MAX_LEVELS);
    let bays = ParseIntegerInput(raw.bays);
    bays = Number.isFinite(bays) && bays >= 1 ? Math.min(bays, RACK_REAL_MAX_BAYS) : null;
    let maxLevels = ParseIntegerInput(raw.maxLevels);
    if (!Number.isFinite(maxLevels) || maxLevels < 1) {
        maxLevels = Math.max(RACK_DRAFT_DEFAULTS.maxLevels, levels);
    }
    maxLevels = Math.max(maxLevels, levels);
    let levelHeight = ParseDecimalInput(raw.levelHeight);
    if (!Number.isFinite(levelHeight) || levelHeight <= 0) {
        levelHeight = RACK_DRAFT_DEFAULTS.levelHeight;
    }
    let positions = ParseIntegerInput(raw.positions);
    if (!Number.isFinite(positions) || positions < 1) {
        positions = RACK_DRAFT_DEFAULTS.positions;
    }
    let blockWidth = ParseDecimalInput(raw.blockWidth);
    if (!Number.isFinite(blockWidth) || blockWidth <= 0) {
        blockWidth = RACK_DRAFT_DEFAULTS.blockWidth;
    }
    const rawWeights = Array.isArray(raw.weights) ? raw.weights : [];
    const weights = Array.from({ length: levels }, (_, index) => {
        const parsed = ParseDecimalInput(rawWeights[index]);
        return Number.isFinite(parsed) && parsed > 0 ? parsed : defaultWeightForLevel(index + 1);
    });
    return {
        name: String(raw.name || '').trim(),
        rack: String(raw.rack || '')
            .trim()
            .toUpperCase(),
        orientation,
        baySide: CoerceBaySideForOrientation(orientation, raw.baySide),
        bays,
        levels,
        maxLevels,
        levelHeight,
        positions,
        blockWidth,
        weights,
    };
}

const FIELD_GRID = 'grid grid-cols-2 gap-2';

/**
 * `initialOpts` = opts normalizados que o WarehouseMap montou ao armar o
 * traçado (defaults + prefixo sugerido). Remonte o painel com `key` a cada
 * traçado NOVO (não a cada re-traçado — refazer o traçado preserva o que já
 * foi digitado). `orientation` é a DERIVADA do traçado corrente (prop, não
 * campo). `traceLabel` = resumo do traçado (ex.: "Vertical · 6 células").
 * `traceBays` = nº de células do traçado (placeholder de "quantos blocos").
 * `submitting` = generate-rack em andamento (trava o form).
 */
const RackDraftPanel = ({
    initialOpts,
    orientation,
    traceLabel = null,
    traceBays = null,
    placeOnly = false,
    error,
    submitting = false,
    onChange,
    onConfirm,
    onCancel,
}) => {
    const { label } = useWarehouseLabels();
    const [form, setForm] = useState(() => ({
        name: initialOpts.name || '',
        rack: initialOpts.rack || '',
        baySide: initialOpts.baySide,
        bays: initialOpts.bays == null ? '' : String(initialOpts.bays),
        levels: String(initialOpts.levels),
        maxLevels: String(initialOpts.maxLevels),
        levelHeight: String(initialOpts.levelHeight),
        positions: String(initialOpts.positions),
        blockWidth: String(initialOpts.blockWidth),
        weights: (Array.isArray(initialOpts.weights) && initialOpts.weights.length
            ? initialOpts.weights
            : RACK_DRAFT_DEFAULTS.weights
        ).map((weight) => String(weight)),
    }));

    // Handler de evento (não updater): calcula o próximo form, commita e sobe
    // a versão normalizada numa tacada — sem efeito colateral dentro do
    // setState. A orientação entra do lado de fora (derivada do traçado).
    const apply = (patch) => {
        const next = { ...form, ...patch };
        setForm(next);
        onChange(NormalizeRackDraft({ ...next, orientation }));
    };

    const setField = (name) => (event) => apply({ [name]: event.target.value });

    // Lado coerente com o eixo derivado: o form pode ter guardado o lado do
    // eixo anterior (re-traçado); a exibição mostra sempre o par válido.
    const baySide = CoerceBaySideForOrientation(orientation, form.baySide);
    const sideOptions = BAY_SIDES_BY_ORIENTATION[orientation] || BAY_SIDES_BY_ORIENTATION.vertical;

    // Mudar andares preserva os pesos digitados e estende com defaults.
    const setLevels = (event) => {
        const raw = event.target.value;
        const parsed = ParseIntegerInput(raw);
        const usable = parsed !== null && !Number.isNaN(parsed) && parsed >= 1 && parsed <= RACK_REAL_MAX_LEVELS;
        apply({
            levels: raw,
            weights: usable
                ? Array.from(
                      { length: parsed },
                      (_, index) => form.weights[index] ?? String(defaultWeightForLevel(index + 1)),
                  )
                : form.weights,
        });
    };

    const setWeight = (index) => (event) => {
        const raw = event.target.value;
        apply({ weights: form.weights.map((weight, i) => (i === index ? raw : weight)) });
    };

    // Pesos SÓ dos andares aéreos: o A1 (térreo) apoia no chão e não tem limite.
    const aerialWeights = form.weights
        .map((weight, index) => ({ weight, level: index + 1, index }))
        .filter((entry) => entry.level > GROUND_LEVEL);

    return (
        <form
            className="space-y-3"
            onSubmit={(event) => {
                event.preventDefault();
                onConfirm();
            }}
        >
            <p className="text-xs font-semibold uppercase tracking-wide text-mono-500 dark:text-mono-400">
                {label(placeOnly ? 'wms.map.rack_place_title' : 'wms.map.rack_draft_title')}
            </p>

            {placeOnly ? (
                <div className="rounded-md bg-mono-100 px-2.5 py-1.5 text-sm dark:bg-mono-800">
                    <span className="text-mono-600 dark:text-mono-400">{label('wms.field.rack')}: </span>
                    <Mono className="font-semibold text-black dark:text-white">{form.rack}</Mono>
                    <span className="ml-2 text-xs text-mono-500 dark:text-mono-400">
                        {`${form.bays} ${label('wms.map.bays_from_registry')}`}
                    </span>
                </div>
            ) : (
                <>
                    <Input
                        label={label('wms.map.rack_prefix')}
                        value={form.rack}
                        onChange={(event) => apply({ rack: event.target.value.toUpperCase() })}
                        maxLength={2}
                        autoComplete="off"
                        disabled={submitting}
                    />
                    <Input
                        label={label('wms.map.rack_name')}
                        value={form.name}
                        onChange={setField('name')}
                        placeholder={form.rack ? `Rack ${form.rack}` : 'Rack'}
                        autoComplete="off"
                        disabled={submitting}
                    />
                </>
            )}

            {/* Traçado (leitura): orientação e comprimento saem dos dois cliques. */}
            {traceLabel ? (
                <p className="rounded-md bg-mono-100 px-2.5 py-1.5 text-xs font-medium text-mono-700 dark:bg-mono-800 dark:text-mono-300">
                    {label('wms.map.trace_summary')}: {traceLabel}
                </p>
            ) : null}

            <Select
                label={label('wms.map.bay_side')}
                value={baySide}
                onChange={(event) => apply({ baySide: event.target.value })}
                disabled={submitting}
            >
                {sideOptions.map((option) => (
                    <option key={option} value={option}>
                        {label(`wms.map.side.${option}`)}
                    </option>
                ))}
            </Select>

            {placeOnly ? null : (
                <>
                    <div className={FIELD_GRID}>
                        {/* Quantos blocos: em branco = o comprimento do traçado; digitado,
                            o corredor estende/encurta a partir do 1º clique. */}
                        <Input
                            label={label('wms.map.bays_count')}
                            type="number"
                            min={1}
                            max={RACK_REAL_MAX_BAYS}
                            value={form.bays}
                            onChange={setField('bays')}
                            placeholder={traceBays ? String(traceBays) : ''}
                            autoComplete="off"
                            disabled={submitting}
                        />
                        <Input
                            label={label('wms.map.levels')}
                            type="number"
                            min={1}
                            max={RACK_REAL_MAX_LEVELS}
                            value={form.levels}
                            onChange={setLevels}
                            disabled={submitting}
                        />
                        <Input
                            label={label('wms.map.max_levels')}
                            type="number"
                            min={1}
                            value={form.maxLevels}
                            onChange={setField('maxLevels')}
                            disabled={submitting}
                        />
                        <Input
                            label={label('wms.map.level_height')}
                            inputMode="decimal"
                            value={form.levelHeight}
                            onChange={setField('levelHeight')}
                            autoComplete="off"
                            disabled={submitting}
                        />
                        <Input
                            label={label('wms.map.positions')}
                            type="number"
                            min={1}
                            value={form.positions}
                            onChange={setField('positions')}
                            disabled={submitting}
                        />
                        {/* UMA largura para todos os blocos do rack (M4-4): esta medida
                            desenha o bloco E vira o vão gravado no cadastro. */}
                        <Input
                            label={label('wms.map.block_width')}
                            inputMode="decimal"
                            value={form.blockWidth}
                            onChange={setField('blockWidth')}
                            autoComplete="off"
                            disabled={submitting}
                        />
                    </div>

                    {/* Pesos POR ANDAR (kg) — vão no generate-rack real. O A1 não entra:
                        térreo não tem limite de peso (o backend recusa). */}
                    <div>
                        <p className="mb-1.5 text-sm font-medium text-mono-700 dark:text-mono-300">
                            {label('wms.generator.weights_title')}
                        </p>
                        <p className="mb-1.5 text-xs text-mono-500 dark:text-mono-400">
                            {label('wms.map.ground_level_no_weight')}
                        </p>
                        {aerialWeights.length > 0 ? (
                            <div className="grid grid-cols-3 gap-2">
                                {aerialWeights.map((entry) => (
                                    <Input
                                        key={`A${entry.level}`}
                                        label={`A${entry.level}`}
                                        inputMode="decimal"
                                        value={entry.weight}
                                        onChange={setWeight(entry.index)}
                                        autoComplete="off"
                                        disabled={submitting}
                                    />
                                ))}
                            </div>
                        ) : null}
                        <p className="mt-1 text-xs text-mono-500 dark:text-mono-400">
                            {label('wms.map.rack_real_note')}
                        </p>
                    </div>
                </>
            )}

            {error ? (
                <p role="alert" className="text-xs font-medium text-danger-600 dark:text-danger-400">
                    {error}
                </p>
            ) : (
                <p className="text-xs text-mono-500 dark:text-mono-400">{label('wms.map.rack_draft_hint')}</p>
            )}

            <div className="flex flex-col gap-2">
                <Button type="submit" size="sm" loading={submitting} disabled={Boolean(error)}>
                    {label(placeOnly ? 'wms.map.place_rack' : 'wms.map.create_rack')}
                </Button>
                <Button type="button" size="sm" variant="secondary" onClick={onCancel} disabled={submitting}>
                    {label('wms.common.cancel')}
                </Button>
            </div>
        </form>
    );
};

export default RackDraftPanel;
